import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Checkbox,
  Container,
  Drawer,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Toolbar,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../components/CustomButton";
import CustomModal from "../../../../components/CustomModal/CustomModal";
import CustomTable from "../../../../components/CustomTable";
import CustomTextField from "../../../../components/CustomTextField";
import TopBar from "../../../../components/TopBar/TopBar";
import { getRoleList } from "../../../../redux/reducers/roleSlice";
import {
  createSchedule,
  deleteSchedule,
  getScheduleList,
  updateSchedule,
} from "../../../../redux/reducers/scheduleSlice";
import { AppColors } from "../../../../util/AppColors";
import DayWiseTimeSlots from "./DayWiseTimeSlots";

const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const consultationTypes = ["Physical", "Virtual", "Home-Visit"];
const tblHdrs = [
  "sNo",
  "name",
  "consultationType",
  "role",
  "timeDay",
  "createdDate",
  "actions",
];

function Schedule() {
  const [search, setSearch] = useState("");
  const [openDrawer, setOpenDrawer] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [delModVisible, setDelModVisible] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [form, setForm] = useState(initialFormState());

  const dispatch = useDispatch();

  const roleList = useSelector((state) =>
    state.role.list.filter((r) => r.type === "role")
  );

  const scheduleList = useSelector((state) => state.schedule.list);

  function initialFormState() {
    return {
      name: "",
      consultationType: [],
      role: "",
      schedule: daysOfWeek.map((day) => ({
        day,
        isSelected: false,
        slots: [
          {
            startTime: dayjs("08:00", "HH:mm"),
            endTime: dayjs("20:00", "HH:mm"),
          },
        ],
      })),
    };
  }

  useEffect(() => {
    dispatch(getRoleList());
    dispatch(getScheduleList());
  }, [dispatch]);

  // Transform API data to component format
  // const transformApiDataToForm = (apiData) => {
  //   // Handle null/undefined apiData
  //   if (!apiData) {
  //     return initialFormState();
  //   }

  //   const consultationType = [];
  //   if (apiData.physical) consultationType.push('Physical');
  //   if (apiData.virtuals) consultationType.push('Virtual');
  //   if (apiData.homeVisit) consultationType.push('Home-Visit');

  //   const schedule = daysOfWeek.map(dayName => {
  //     const dayData = apiData.gobalDayRequest?.find(
  //       day => day.day.toLowerCase() === dayName.toLowerCase().substring(0, 3)
  //     );

  //     return {
  //       day: dayName,
  //       isSelected: dayData?.status || false,
  //       slots: dayData?.times?.map(time => ({
  //         startTime: dayjs(time.startTime, 'HH:mm'),
  //         endTime: dayjs(time.endTime, 'HH:mm')
  //       })) || [{ startTime: dayjs('08:00', 'HH:mm'), endTime: dayjs('20:00', 'HH:mm') }]
  //     };
  //   });

  //   return {
  //     name: apiData.templateName || '',
  //     consultationType,
  //     role: apiData.role || '',
  //     schedule
  //   };
  // };
  // Transform API data to component format
  const transformApiDataToForm = (apiData) => {
    // Handle null/undefined apiData
    if (!apiData) {
      return initialFormState();
    }

    const consultationType = [];
    if (apiData.physical) consultationType.push("Physical");
    if (apiData.virtuals) consultationType.push("Virtual");
    if (apiData.homeVisit) consultationType.push("Home-Visit");

    const schedule = daysOfWeek.map((dayName) => {
      // Fix: Use 'globalDay' instead of 'gobalDayRequest' and match the correct day format
      const dayData = apiData.globalDay?.find(
        (day) => day.day.toLowerCase() === dayName.toLowerCase().substring(0, 3)
      );

      return {
        day: dayName,
        isSelected: dayData?.status || false,
        slots: dayData?.times?.map((time) => ({
          startTime: dayjs(time.startTime, "HH:mm"),
          endTime: dayjs(time.endTime, "HH:mm"),
        })) || [
          {
            startTime: dayjs("08:00", "HH:mm"),
            endTime: dayjs("20:00", "HH:mm"),
          },
        ],
      };
    });

    return {
      name: apiData.templateName || "",
      consultationType,
      role: apiData.role || "",
      schedule,
    };
  };
  // Transform component data to API format
  const transformFormToApiData = (formData) => {
    const dayMapping = {
      Sun: "sun",
      Mon: "mon",
      Tue: "tue",
      Wed: "wed",
      Thu: "thu",
      Fri: "fri",
      Sat: "sat",
    };

    return {
      physical: formData.consultationType.includes("Physical"),
      virtuals: formData.consultationType.includes("Virtual"),
      homeVisit: formData.consultationType.includes("Home-Visit"),
      templateName: formData.name,
      role: formData.role,
      gobalDayRequest: formData.schedule.map((daySchedule) => ({
        day: dayMapping[daySchedule.day],
        duration: 2, // Default duration, you might want to make this configurable
        status: daySchedule.isSelected,
        times: daySchedule.slots.map((slot) => ({
          startTime: slot.startTime.format("HH:mm"),
          endTime: slot.endTime.format("HH:mm"),
        })),
      })),
    };
  };

  const handleCloseDrawer = () => {
    setOpenDrawer(false);
    setEditIndex(null);
    setForm(initialFormState());
  };

  const handleAddOrUpdate = () => {
    if (!form.name) return;

    const apiData = transformFormToApiData(form);

    if (editIndex !== null) {
      const scheduleToUpdate = scheduleList[editIndex];
      dispatch(
        updateSchedule({
          scheduleId: scheduleToUpdate.scheduleId, // Assuming API returns scheduleId
          scheduleData: apiData,
        })
      ).then(() => {
        dispatch(getScheduleList());
        handleCloseDrawer();
      });
    } else {
      dispatch(createSchedule(apiData)).then(() => {
        dispatch(getScheduleList());
        handleCloseDrawer();
      });
    }
  };

  const handleEdit = (index) => {
    const item = scheduleList[index];
    const transformedData = transformApiDataToForm(item);
    setForm(transformedData);
    setEditIndex(index);
    setOpenDrawer(true);
  };

  const handleDeleteClick = (index) => {
    setDeleteIndex(index);
    setDelModVisible(true);
  };

  const handleDelConfirm = () => {
    const scheduleToDelete = scheduleList[deleteIndex];
    dispatch(deleteSchedule({ scheduleId: scheduleToDelete.scheduleId })).then(
      () => {
        dispatch(getScheduleList());
        setDelModVisible(false);
        setDeleteIndex(null);
      }
    );
  };

  const handleDelModClose = () => {
    setDelModVisible(false);
    setDeleteIndex(null);
  };

  const handleCheckboxChange = (dayIndex, checked) => {
    const updated = [...form.schedule];
    updated[dayIndex].isSelected = checked;
    setForm({ ...form, schedule: updated });
  };

  const handleTimeChange = (field, value, dayIndex, slotIndex) => {
    const updated = [...form.schedule];
    updated[dayIndex].slots[slotIndex][field] = value;
    setForm({ ...form, schedule: updated });
  };

  const handleAddSlot = (dayIndex) => {
    const updated = [...form.schedule];
    updated[dayIndex].slots.push({
      startTime: dayjs("08:00", "HH:mm"),
      endTime: dayjs("20:00", "HH:mm"),
    });
    setForm({ ...form, schedule: updated });
  };

  const handleRemoveSlot = (dayIndex, slotIndex) => {
    const updated = [...form.schedule];
    updated[dayIndex].slots.splice(slotIndex, 1);
    setForm({ ...form, schedule: updated });
  };

  const handleConsultationChange = (type) => {
    const updated = [...form.consultationType];
    const exists = updated.includes(type);
    setForm({
      ...form,
      consultationType: exists
        ? updated.filter((t) => t !== type)
        : [...updated, type],
    });
  };

  // Transform scheduleList for display
  const displayRows = scheduleList
    .map((item) => transformApiDataToForm(item))
    .filter(Boolean);

  const filteredRows = displayRows.filter((row) =>
    row.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      {/* Header */}
      <TopBar>
        <Container maxWidth="xl">
          <Toolbar variant="regular">
            <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
              <div className="flex-row">
                <div className="top-row-cen-con w100Per">
                  <div className="w30Per">
                    <CustomTextField
                      placeholder="Search"
                      name="search"
                      fullWidth
                      handleChange={(e) => setSearch(e.target.value)}
                      value={search}
                      backgroundColor={AppColors.lightBlue2}
                    />
                  </div>
                  <div className="ml20">
                    <CustomButton
                      text="Create"
                      onClick={() => setOpenDrawer(true)}
                    />
                  </div>
                </div>
              </div>
            </Grid>
          </Toolbar>
        </Container>
        <Typography className="font-medium fs12 gray28 ph20">
          Total Schedule: {filteredRows?.length}
        </Typography>
      </TopBar>

      {/* Table */}
      <CustomTable
        columns={tblHdrs}
        grey
        datas={filteredRows?.map((li, i) => ({
          sNo: i + 1,
          name: li?.name,
          consultationType: li?.consultationType?.join(", "),
          role: li?.role,
          timeDay: li?.schedule
            ?.filter((d) => d?.isSelected)
            ?.map((day, dIdx) => (
              <div key={dIdx}>
                <strong>{day.day}:</strong>{" "}
                {day.slots.map((slot, sIdx) => (
                  <span key={sIdx}>
                    {slot.startTime?.format?.("HH:mm") || slot.startTime} -{" "}
                    {slot.endTime?.format?.("HH:mm") || slot.endTime}
                    {sIdx < day.slots.length - 1 ? ", " : ""}
                  </span>
                ))}
              </div>
            )),
          createdDate: scheduleList[i]?.createdDate
            ? new Date(scheduleList[i]?.createdDate)?.toLocaleDateString()
            : "-",
          actions: (
            <div>
              <IconButton
                color="primary"
                size="small"
                onClick={() => handleEdit(i)}
              >
                <EditIcon />
              </IconButton>
              <IconButton
                color="error"
                size="small"
                onClick={() => handleDeleteClick(i)}
              >
                <DeleteIcon />
              </IconButton>
            </div>
          ),
        }))}
      />

      {/* Drawer */}
      <Drawer anchor="right" open={openDrawer} onClose={handleCloseDrawer}>
        <Box p={3} width={360}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6" color="primary">
              {editIndex !== null ? "Edit Schedule" : "Create Schedule"}
            </Typography>
            <IconButton onClick={handleCloseDrawer} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <TextField
            fullWidth
            label="Schedule Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            margin="normal"
          />

          <DayWiseTimeSlots
            values={form.schedule}
            onDaySelect={handleCheckboxChange}
            onSlotChange={handleTimeChange}
            onAddSlot={handleAddSlot}
            onRemoveSlot={handleRemoveSlot}
          />

          <Typography variant="subtitle2" mt={3}>
            Consultation Type:
          </Typography>
          {consultationTypes.map((type) => (
            <Box key={type} display="flex" alignItems="center" gap={1}>
              <Checkbox
                checked={form.consultationType.includes(type)}
                onChange={() => handleConsultationChange(type)}
              />
              <Typography>{type}</Typography>
            </Box>
          ))}

          <TextField
            select
            label="Role"
            fullWidth
            margin="normal"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          >
            {roleList.length === 0 ? (
              <MenuItem disabled>No roles available</MenuItem>
            ) : (
              roleList.map((role) => (
                <MenuItem key={role.roleId} value={role.roleName}>
                  {role.roleName}
                </MenuItem>
              ))
            )}
          </TextField>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleAddOrUpdate}
          >
            {editIndex !== null ? "Update" : "Add"}
          </Button>
        </Box>
      </Drawer>

      {/* Delete Modal */}
      <CustomModal
        open={delModVisible}
        onClose={handleDelModClose}
        header="Delete"
        modal
        modalWidth={30}
      >
        <Typography className="flex-center txt-regular fs14">
          Are you sure you want to delete this record?
        </Typography>
        <div className="flex-center mb10 mt15">
          <div>
            <CustomButton text="Cancel" onClick={handleDelModClose} />
          </div>
          <div className="ml10">
            <CustomButton redBtn text="Confirm" onClick={handleDelConfirm} />
          </div>
        </div>
      </CustomModal>
    </Box>
  );
}

export default Schedule;
